import { isEmpty, keys, pick } from 'ramda';
import type { Location, NodeEntry } from 'slate';
import { Editor, Path, Text, Transforms } from 'slate';

import type { UUID } from '../../types/type-helpers';
import type {
  AlineText,
  AlineTextFormats,
  DynamicBlockElement,
  GlobalStylesMap,
  ParagraphElement,
  ParagraphFormatEdit,
  ParagraphFormatKey,
  ParagraphProps,
  ParagraphStyleVariant,
  PartialParagraphProps,
  SelBoundaryNode,
  StyleVariantEdit,
  SuggestedParagraphEdit,
  TableCellElement,
} from '../internal';
import {
  AlineTransforms,
  createDefaultParagraph,
  EditingMode,
  isDynamicBlockElement,
  isParagraphElement,
  isTableCellElement,
  nodeIsSelBoundary,
  Queries,
} from '../internal';

const allParagraphFormatKeys = keys({
  alignment: true,
  borders: true,
  indentationFirstLine: true,
  indentationLeft: true,
  indentationRight: true,
  lineSpacing: true,
  listProps: true,
  shade: true,
  spacingAfter: true,
  spacingBefore: true,
} satisfies Record<ParagraphFormatKey, true>);

const ParagraphTransforms = {
  /**
   * Applies a style variant's styles to all text nodes inside a paragraph.
   * Note: User-applied styles are kept and only inherited styles are overridden on the text.
   */
  applyStyleVariantToText: (
    editor: Editor,
    options: {
      editingMode: EditingMode;
      paragraphEntry: NodeEntry<ParagraphElement>;
      styleVariant: ParagraphStyleVariant;
      isUpdateToMatch?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { isUpdateToMatch = false, paragraphEntry, styleVariant } = options;
      const [paragraph, paragraphPath] = paragraphEntry;
      const styleSheet = Queries.activeStyleSheet(editor);
      const variantStyles = styleSheet[styleVariant].styles;
      const activeStyleVariant = Queries.renderedParagraphStyleVariant(paragraph);
      const newStyleVariantEqualsExisting = styleVariant === activeStyleVariant;
      const paragraphRange = Editor.range(editor, paragraphPath);
      const textEntries = Editor.nodes<AlineText>(editor, {
        at: paragraphRange,
        match: Text.isText,
      });

      for (const [textNode, textPath] of textEntries) {
        const stylesToKeep = keys(variantStyles).reduce((appliedStyles, key): AlineTextFormats => {
          const formatValue = textNode[key];

          if (!formatValue || !('origin' in formatValue)) return appliedStyles;

          return formatValue.origin === 'USER_APPLIED'
            ? { ...appliedStyles, [key]: formatValue }
            : { ...appliedStyles, [key]: variantStyles[key] };
        }, variantStyles);

        const newTextStyles =
          newStyleVariantEqualsExisting && !isUpdateToMatch ? variantStyles : stylesToKeep;

        Transforms.setNodes(editor, newTextStyles, {
          at: textPath,
        });
      }
    });
  },

  /**
   * Check if the last element in the container is a paragraph and if not, create one and select it.
   * @returns {boolean} whether a paragraph was created
   */
  ensureParagraphAtEnd(
    editor: Editor,
    options: {
      authorId: string;
      containerEntry: NodeEntry<SelBoundaryNode>;
      editingMode: EditingMode;
    }
  ): boolean {
    const { authorId, containerEntry, editingMode } = options;
    const [container, containerPath] = containerEntry;

    const lastElement = container.children.at(-1);
    if (!lastElement || isParagraphElement(lastElement)) return false;

    const lastElementPath = containerPath.concat(container.children.length - 1);

    AlineTransforms.insertParagraph(editor, {
      at: lastElementPath,
      authorId,
      direction: 'below',
      editingMode,
    });

    return true;
  },

  /**
   * Increases or decreases paragraph indentation by a number of inches.  Defaults to .5 as that is the most common unit.
   */
  indentParagraph: (
    editor: Editor,
    options: {
      at: Path;
      authorId: string;
      direction: 'increase' | 'decrease';
      editingMode: EditingMode;
      inches?: number;
    }
  ): Error | void => {
    const { at, authorId, direction, editingMode, inches = 0.5 } = options;
    const [paragraphEntry] = Queries.paragraphsAtLocation(editor, { at, listMarker: true });
    if (!paragraphEntry) return;

    const [paragraph, paragraphPath] = paragraphEntry;
    const activeLeftIndentIn = Queries.renderedParagraphLeftIndent(paragraph);
    const newIndentation =
      direction === 'increase' ? activeLeftIndentIn + inches : activeLeftIndentIn - inches;

    return AlineTransforms.setParagraphProps(
      editor,
      editingMode,
      authorId,
      { indentationLeft: newIndentation },
      { at: paragraphPath }
    );
  },

  /**
   * Insert a paragraph above/below a location.
   */
  insertParagraph: (
    editor: Editor,
    options: { at: Path; authorId: UUID; direction: 'above' | 'below'; editingMode: EditingMode }
  ) => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at, authorId, direction, editingMode } = options;
      const insertPath = direction === 'above' ? at : Path.next(at);
      const point = direction === 'above' ? Editor.start(editor, at) : Editor.end(editor, at);
      const [{ suggestedEdits, text, ...textProps }] = Editor.leaf(editor, point);

      AlineTransforms.insertContentNodes(editor, {
        at: insertPath,
        authorId,
        editingMode,
        merge: false,
        nodes: [createDefaultParagraph({ textProps })],
      });
    });
  },

  /**
   * Removes the paragraph above the selection.  Useful for removing paragraphs/newlines before/after tables, DCs, etc.
   */
  removeParagraphAboveSelection: (editor: Editor): void => {
    const paragraphEntryAboveSel = Editor.above(editor, { match: isParagraphElement });
    const ancestorEntry = Editor.above<SelBoundaryNode | DynamicBlockElement | TableCellElement>(
      editor,
      {
        match: n => nodeIsSelBoundary(n) || isDynamicBlockElement(n) || isTableCellElement(n),
        voids: true,
      }
    );
    if (!ancestorEntry || !paragraphEntryAboveSel) return;

    const [ancestor] = ancestorEntry;
    const isLastBlockInAncestor = ancestor.children.length === 1;
    // Can't remove last paragraph w/in containing block.
    if (isLastBlockInAncestor) return;

    const [, path] = paragraphEntryAboveSel;
    Transforms.removeNodes(editor, { at: path, voids: true });
  },
  /**
   * Sets paragraph props (e.g., alignment, line spacing, indentation, variant,
   * etc.), taking into account editing mode.
   */
  setParagraphProps: (
    editor: Editor,
    editingMode: EditingMode,
    authorId: string,
    props: PartialParagraphProps,
    options: { at?: Location } = {}
  ): Error | void => {
    const { at = editor.selection } = options;
    if (!at) return;

    for (const [paragraph] of Queries.paragraphsAtLocation(editor, { at, listMarker: true })) {
      if (!Queries.paragraphPropsValid(editor, paragraph, props))
        return new Error('Paragraph props invalid.');
    }

    AlineTransforms.withoutNormalizing(editor, () => {
      switch (editingMode) {
        case EditingMode.SUGGESTING: {
          const paragraphEdits = generateParagraphEdits(authorId, props);
          if (!isEmpty(paragraphEdits)) {
            for (const paragraphEdit of paragraphEdits)
              AlineTransforms.suggestParagraphEdit(editor, { at, editingMode, paragraphEdit });
          }
          return;
        }
        case EditingMode.EDITING: {
          const variantProp = props.variant;
          const nodeProps = toParagraphNodeProps(props);
          const paragraphEntries = Queries.paragraphsAtLocation(editor, { at, listMarker: true });

          for (const [paragraph, paragraphPath] of paragraphEntries) {
            AlineTransforms.removeConflictingSuggestions(editor, {
              editingMode,
              entry: [paragraph, paragraphPath],
              props: nodeProps,
            });
            Transforms.setNodes(editor, nodeProps, { at: paragraphPath });

            if (variantProp) {
              AlineTransforms.applyStyleVariantToText(editor, {
                editingMode,
                paragraphEntry: [paragraph, paragraphPath],
                styleVariant: variantProp,
              });
            }
          }
          return;
        }
      }
    });
  },
  /**
   * Split a paragraph.
   */
  splitParagraph: (editor: Editor, options: { at?: Location } = {}): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection } = options;
      if (!at) return;

      Transforms.splitNodes(editor, {
        always: true,
        at,
        match: isParagraphElement,
      });
    });
  },

  /**
   * Updates the relevant style variant (e.g., 'style-normal') to match the formats at the selection, which involves
   * updating all text in the document to include the new styles and updating the document style sheet to match
   * the selected styles.
   */
  updateStylesToMatch: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      styleVariant: ParagraphStyleVariant;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { authorId, editingMode, styleVariant } = options;
      const activeFormats = Queries.consistentFormats(editor);
      if (!editor.selection || !activeFormats) return;

      const mergedStyles = Queries.mergedStyles(editor, styleVariant, activeFormats);

      /**
       * Update the document style sheet with the newly-applied styles.
       * IMPORTANT:  Do this at the top of the function as other transforms below rely on the style sheet
       * being up to date.
       */
      const newStyles: Partial<GlobalStylesMap> = {
        [styleVariant]: mergedStyles,
      };

      AlineTransforms.updateStyleSheet(editor, { authorId, editingMode, newStyles });

      const paragraphEntriesAtSelection = Queries.paragraphsAtLocation(editor, {
        listMarker: true,
      });

      /**
       * Set the style variant of the paragraphs at the selection first before updating all paragraphs with the same
       * style variant to match the new styles.
       */
      for (const [, paragraphPath] of paragraphEntriesAtSelection) {
        if (editingMode === EditingMode.SUGGESTING) {
          const styleVariantEdit: StyleVariantEdit = {
            authorId,
            type: 'variant',
            value: { variant: styleVariant },
          };

          AlineTransforms.suggestParagraphEdit(editor, {
            at: paragraphPath,
            editingMode,
            paragraphEdit: styleVariantEdit,
          });

          continue;
        }

        Transforms.setNodes(editor, { variant: styleVariant }, { at: paragraphPath });
      }

      const fullEditorRange = Queries.fullEditorRange(editor);

      const paragraphEntries = Editor.nodes<ParagraphElement>(editor, {
        at: fullEditorRange,
        match: n =>
          isParagraphElement(n) && Queries.renderedParagraphStyleVariant(n) === styleVariant,
      });

      /**
       * Update all paragraphs in the document that have the relevant style variant with the new styles
       * for that variant.  We do NOT need to do anything different here in suggested edits mode.
       */
      for (const [paragraph, paragraphPath] of paragraphEntries) {
        AlineTransforms.applyStyleVariantToText(editor, {
          editingMode,
          isUpdateToMatch: true,
          paragraphEntry: [paragraph, paragraphPath],
          styleVariant,
        });
      }
    });
  },
};

/**
 * Type-level bridge from edit-intent paragraph props to node props accepted by
 * `Transforms.setNodes`
 * TODO: Remove this function once https://github.com/ianstormtaylor/slate/pull/6083 is merged
 */
export function toParagraphNodeProps(props: PartialParagraphProps): Partial<ParagraphProps> {
  const { listProps, ...rest } = props;
  if (listProps === null) return { ...rest, listProps: undefined };
  if (listProps === undefined) return rest;
  return { ...rest, listProps };
}

function generateParagraphEdits(
  authorId: string,
  props: PartialParagraphProps
): SuggestedParagraphEdit[] {
  const propsKeys = keys(props);
  const variantProp = props.variant;
  const paragraphFormatKeys = propsKeys.filter(isParagraphFormatKey);
  const suggestedParagraphEdits: SuggestedParagraphEdit[] = [];

  if (!isEmpty(paragraphFormatKeys)) {
    const paragraphFormatEdit: ParagraphFormatEdit = {
      authorId,
      type: 'paragraphFormat',
      value: pick(paragraphFormatKeys, props),
    };

    suggestedParagraphEdits.push(paragraphFormatEdit);
  }

  if (variantProp) {
    const variantEdit: StyleVariantEdit = {
      authorId,
      type: 'variant',
      value: { variant: variantProp },
    };

    suggestedParagraphEdits.push(variantEdit);
  }

  return suggestedParagraphEdits;
}

function isParagraphFormatKey(key: string): key is ParagraphFormatKey {
  return allParagraphFormatKeys.includes(key as ParagraphFormatKey);
}

export default ParagraphTransforms;
